// Provider-agnostic connect wizard: pick an enabled vendor, then hand off to
// the vendor's OAuth flow. No per-vendor JSX — everything renders from the
// /api/payroll/vendors catalog payload.

import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ExternalLink, ShieldCheck } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

export interface PayrollVendor {
  vendorKey: string;
  name: string;
  description: string | null;
  logoColor: string | null;
  logoInitials: string | null;
  setupComplexity: string | null;
}

interface PayrollConnectWizardProps {
  clientId: number;
  open: boolean;
  onClose: () => void;
  /** Existing connections so a pending/disconnected row is reused instead of a 409. */
  existingConnections: Array<{ id: number; vendorKey: string }>;
}

export default function PayrollConnectWizard({ clientId, open, onClose, existingConnections }: PayrollConnectWizardProps) {
  const { toast } = useToast();
  const [selected, setSelected] = useState<PayrollVendor | null>(null);

  const { data: vendors = [], isLoading } = useQuery<PayrollVendor[]>({
    queryKey: ["/api/payroll/vendors"],
    enabled: open,
  });

  const connectMutation = useMutation({
    mutationFn: async (vendor: PayrollVendor) => {
      let connectionId = existingConnections.find((c) => c.vendorKey === vendor.vendorKey)?.id;
      if (!connectionId) {
        const res = await apiRequest("POST", `/api/clients/${clientId}/payroll-connections`, {
          vendorKey: vendor.vendorKey,
        });
        connectionId = (await res.json()).id;
      }
      const returnTo = window.location.pathname + window.location.search;
      const res = await apiRequest("POST", `/api/payroll-connections/${connectionId}/oauth/start`, { returnTo });
      return (await res.json()) as { authUrl: string };
    },
    onSuccess: ({ authUrl }) => {
      window.location.assign(authUrl);
    },
    onError: (e: any) =>
      toast({ variant: "destructive", title: "Failed to start connection", description: e?.message }),
  });

  const close = () => {
    setSelected(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="sm:max-w-lg">
        {!selected ? (
          <>
            <DialogHeader>
              <DialogTitle>Connect a payroll provider</DialogTitle>
              <DialogDescription>
                Choose the payroll system this company uses. Employee census data will sync
                automatically once connected.
              </DialogDescription>
            </DialogHeader>
            {isLoading ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Loading providers…</p>
            ) : vendors.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                No payroll providers are enabled yet. Contact your administrator.
              </p>
            ) : (
              <div className="grid gap-2 py-2">
                {vendors.map((v) => (
                  <button
                    key={v.vendorKey}
                    className="flex items-center gap-3 p-3 border rounded-lg text-left hover:bg-muted/50 transition-colors"
                    onClick={() => setSelected(v)}
                  >
                    <div
                      className="h-10 w-10 rounded-lg flex items-center justify-center text-white font-semibold shrink-0"
                      style={{ backgroundColor: v.logoColor || "#6366f1" }}
                    >
                      {v.logoInitials || v.name.slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium">{v.name}</p>
                      {v.description && (
                        <p className="text-sm text-muted-foreground truncate">{v.description}</p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setSelected(null)}>
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                Connect {selected.name}
              </DialogTitle>
              <DialogDescription>You'll be redirected to {selected.name} to authorize access.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="flex items-start gap-3 p-3 bg-muted/40 rounded-md">
                <ShieldCheck className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                <div className="text-sm space-y-1">
                  <p>
                    A <strong>{selected.name} company administrator</strong> must sign in to approve the
                    connection — have their credentials ready.
                  </p>
                  <p className="text-muted-foreground">
                    Administr only receives read access to the employee census. Nothing is written back
                    to {selected.name}.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={close}>Cancel</Button>
                <Button onClick={() => connectMutation.mutate(selected)} disabled={connectMutation.isPending}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  {connectMutation.isPending ? "Redirecting…" : `Continue to ${selected.name}`}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
